/**
 * Client-side permission flags for action visibility (defense in depth).
 *
 * The backend AuthzService is the single source of truth and the enforcement
 * point: it returns a `capabilities` object on each resource computed from the
 * SAME predicates it enforces on mutations. The UI renders from those flags so
 * it can never offer an action the server would reject.
 *
 * If `capabilities` is ever absent (older response), we fall back to deriving
 * the same rules from is_payer/is_payee/status so nothing silently breaks.
 */

export function milestoneCaps(contract, milestone) {
  if (milestone?.capabilities) return milestone.capabilities;
  const payer = !!contract?.is_payer;
  const payee = !!contract?.is_payee;
  const party = payer || payee;
  const s = milestone?.status;
  const revPending = !!milestone?.pending_revision;
  return {
    can_fund: payer && contract?.status === "Active" && s === "Draft" && !revPending,
    can_submit: payee && s === "Funded" && !revPending,
    can_approve: payer && s === "Submitted" && !revPending,
    can_request_changes: payer && s === "Submitted" && !revPending,
    can_release: payer && s === "Approved" && !revPending,
    can_dispute: party && ["Funded", "Submitted", "Approved"].includes(s) && !revPending,
    can_invoice: payee && ["Submitted", "Approved"].includes(s) && !revPending,
    can_cancel: payer && s === "Draft" && !revPending,
  };
}

export function contractCaps(contract) {
  if (contract?.capabilities) return contract.capabilities;
  const payer = !!contract?.is_payer;
  const party = payer || !!contract?.is_payee;
  const creator = contract?.created_by_user_id && contract.created_by_user_id === contract?.viewer_user_id;
  const acceptable = ["Pending", "Draft"].includes(contract?.status);
  return {
    is_payer: payer,
    is_payee: !!contract?.is_payee,
    can_add_milestone:
      (payer && ["Draft", "Pending", "Active"].includes(contract?.status)) ||
      (creator && ["Draft", "Pending"].includes(contract?.status)),
    can_accept: acceptable && party && !creator,
    can_decline: acceptable && party && !creator,
  };
}

export function invoiceCaps(invoice) {
  if (invoice?.capabilities) return invoice.capabilities;
  const payer = !!invoice?.is_payer;
  const payee = !!invoice?.is_payee;
  const s = invoice?.status;
  return {
    is_payer: payer,
    is_payee: payee,
    can_send: payee && s === "Draft",
    can_cancel: payee && !["Paid", "Cancelled"].includes(s),
    can_pay: payer && !["Paid", "Cancelled", "Disputed"].includes(s),
    can_dispute: payer && ["Sent", "Viewed", "Overdue"].includes(s),
  };
}

export function disputeCaps(dispute, user) {
  if (dispute?.capabilities) return dispute.capabilities;
  return { can_resolve: user?.role === "admin" && dispute?.status !== "Resolved" };
}
